import React from 'react'
import { Flex, Transition } from '@sendoutcards/quantum-design-ui'
import {
  useActions,
  usePersistedUserData,
  useQueryParams,
  useRedirectUriBuilder,
  useRoute,
  useState,
} from 'src/hooks'

import { AsYouType, CountryCode, formatNumber } from 'libphonenumber-js'
import VerifyPhoneNumberSuccess from './VerifyPhoneNumberSuccess'
import CodeVerification from './CodeVerification'
import PhoneNumberEntry from './PhoneNumberEntry'
import IncorrectCode from './IncorrectCode'
import Result from 'src/utils/Result'
import TimedSuccess from './TimedSuccess'
import {
  PhoneNumberInput,
  RequestPhoneNumberVerificationFailureType,
  VerifyPhoneNumberFailureFragment,
  VerifyPhoneNumberSuccessFragment,
} from 'src/graphql/generated/graphql'
import { PromoRoute } from 'src/promo/routes/PromoRoute'
import {
  useGlobalSettings,
  useRequestPhoneNumberVerification,
} from 'src/react_query'
import { useRecaptcha } from 'src/hooks/useRecaptchaEnterprise'

export const getFormattedNumber = (countryCode: string, text: string) => {
  const formatter = new AsYouType(countryCode as CountryCode)
  const preFormattedNumber = formatter.input(text)

  const finalFormattedNumber = formatter.isValid()
    ? formatNumber(
        formatter.getNationalNumber(),
        countryCode as CountryCode,
        'NATIONAL',
      )
    : preFormattedNumber

  return finalFormattedNumber.endsWith(')')
    ? finalFormattedNumber.slice(0, -1)
    : finalFormattedNumber
}

type Step =
  | { type: 'entry'; phoneNumber?: PhoneNumberInput; failureMessage?: string }
  | { type: 'verification'; phoneNumber: PhoneNumberInput }
  | { type: 'incorrectCode'; phoneNumber: PhoneNumberInput }
  | { type: 'success'; success: VerifyPhoneNumberSuccessFragment }
  | { type: 'timedSuccess'; success: VerifyPhoneNumberSuccessFragment }

const PhoneNumberVerification = (props: {
  loginPhoneNumber: PhoneNumberInput | null
  sponsorGenealogyIdOrSlug?: string
  isCheckingOut?: boolean
  onSuccess: (success: VerifyPhoneNumberSuccessFragment) => void
  onDeclinePhoneNumber?: () => void
}) => {
  const {
    loginPhoneNumber,
    sponsorGenealogyIdOrSlug,
    isCheckingOut,
    onDeclinePhoneNumber,
  } = props

  const route = useRoute()
  const actions = useActions()
  const queryParams = useQueryParams()
  const { controlData } = usePersistedUserData()
  const [step, setStep] = useState<Step>(
    loginPhoneNumber && !controlData.isLoggedIn
      ? { type: 'verification', phoneNumber: loginPhoneNumber }
      : {
          type: 'entry',
          phoneNumber: loginPhoneNumber ? loginPhoneNumber : undefined,
        },
  )
  const [formattedNumber, setFormattedNumber] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [phoneVerificationError, setPhoneVerificationError] = useState<
    VerifyPhoneNumberFailureFragment | undefined
  >(undefined)
  const [hasSMSOptIn, setHasSMSOptIn] = useState<boolean | undefined>()
  const redirectUriBuilder = useRedirectUriBuilder()
  const { data: globalSettings } = useGlobalSettings()
  const isVerificationLevelOneEnabled =
    globalSettings?.verificationLevelOneEnabled ?? false

  const recaptcha = useRecaptcha(!isVerificationLevelOneEnabled)
  const handleFailureMessage = (
    failureMessage: string,
    phoneNumberInput: PhoneNumberInput,
  ) => {
    setStep({
      type: 'entry',
      phoneNumber: loginPhoneNumber ?? phoneNumberInput,
      failureMessage,
    })
  }
  const requestPhoneNumberVerification = useRequestPhoneNumberVerification()
  const onVerificationRequested = (phoneNumber: PhoneNumberInput) =>
    setStep({ type: 'verification', phoneNumber })

  const onRequestVerificationCode = async (
    phoneNumberInput: PhoneNumberInput,
    hasSMSOptIn?: boolean,
  ) => {
    try {
      setHasSMSOptIn(hasSMSOptIn)
      const phoneNumberFormatted = getFormattedNumber(
        phoneNumberInput.countryIsoCode,
        phoneNumberInput.nationalNumber,
      )
      setFormattedNumber(phoneNumberFormatted)
      const token =
        process.env.REACT_APP_RECAPTCHA_KEY && isVerificationLevelOneEnabled
          ? await recaptcha.getToken('verification_level_one')
          : undefined

      setIsLoading(true)
      const result = await requestPhoneNumberVerification.mutateAsync({
        phoneNumber: phoneNumberInput,
        recaptchaToken: token,
      })
      if (result.__typename === 'RequestPhoneNumberVerificationSuccess') {
        onVerificationRequested(phoneNumberInput)
      } else if (
        result.__typename === 'RequestPhoneNumberVerificationFailure' &&
        result.failureType ===
          RequestPhoneNumberVerificationFailureType.PhoneNumberBlocked
      ) {
        handleFailureMessage(
          `Phone Number ${phoneNumberFormatted} is blocked. Please contact support.`,
          phoneNumberInput,
        )
      } else if (
        result.__typename === 'RequestPhoneNumberVerificationFailure' &&
        result.failureType ===
          RequestPhoneNumberVerificationFailureType.InvalidLineType
      ) {
        handleFailureMessage(
          `Phone Number ${phoneNumberFormatted}: ${result.failureMessage}. Please contact support.`,
          phoneNumberInput,
        )
      } else {
        handleFailureMessage(result.failureMessage, phoneNumberInput)
      }
    } catch (error) {
      handleFailureMessage(
        error?.toString() ?? 'Failed to request phone number verification.',
        phoneNumberInput,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onFailure = (failure: VerifyPhoneNumberFailureFragment) => {
    if (step.type === 'verification') {
      setPhoneVerificationError(failure)
      setStep({
        type: 'incorrectCode',
        phoneNumber: step.phoneNumber,
      })
    }
  }

  const onSuccess = (
    success: VerifyPhoneNumberSuccessFragment,
    possibleRedirectUri: string | null,
  ) => {
    if (
      success.verifiedAccount.username &&
      loginPhoneNumber &&
      !controlData.isLoggedIn
    ) {
      if (queryParams.isJoiningAffiliate) {
        actions.loadedUser(Result(success.verifiedAccount))
        return
      }
      actions.loginDone(Result(success.verifiedAccount))
      if (route.subroute.path === PromoRoute.path) {
        return
      }
      const redirectUri =
        possibleRedirectUri || redirectUriBuilder(success.verifiedAccount)
      if (redirectUri) {
        window.location.href = redirectUri
      } else {
        actions.openAccount()
      }
    }
    if (isCheckingOut) {
      actions.loadedUser(Result(success.verifiedAccount))
      setStep({ type: 'timedSuccess', success })
    } else if (
      !success.verifiedAccount.isQualifiedForFirstFreeCard &&
      !success.verifiedAccount.username
    ) {
      actions.loginDone(Result(success.verifiedAccount))
      actions.openAccount()
      return
    } else {
      setStep({ type: 'success', success })
    }
  }

  return (
    <Flex
      height="100%"
      width="100%"
      minHeight="500px"
      justifyContent="center"
      alignItems="center"
    >
      {isLoading && (
        <Transition
          isLoading={isLoading}
          title="Sending Code To:"
          subtitle={formattedNumber}
        />
      )}
      {!isLoading && step.type === 'entry' && (
        <PhoneNumberEntry
          requestVerificationCode={onRequestVerificationCode}
          phoneNumber={step.phoneNumber}
          failureMessage={step.failureMessage}
          onDeclinePhoneNumber={onDeclinePhoneNumber}
        />
      )}
      {!isLoading && step.type === 'verification' && (
        <CodeVerification
          phoneNumber={step.phoneNumber}
          hasSmsOptIn={hasSMSOptIn}
          onSuccess={onSuccess}
          onFailure={onFailure}
          sponsorGenealogyIdOrSlug={sponsorGenealogyIdOrSlug}
          onNeedNewCode={(phoneNumber: PhoneNumberInput) => {
            if (loginPhoneNumber) {
              actions.openPhoneNumberVerification(phoneNumber, true)
            } else {
              setStep({
                type: 'entry',
                phoneNumber,
              })
            }
          }}
        />
      )}
      {step.type === 'incorrectCode' && (
        <IncorrectCode
          phoneNumber={step.phoneNumber}
          getNewCode={phoneNumber =>
            setStep({ type: 'entry', phoneNumber: phoneNumber ?? undefined })
          }
          tryAgain={() =>
            setStep({ type: 'verification', phoneNumber: step.phoneNumber })
          }
          failure={phoneVerificationError}
        />
      )}
      {step.type === 'success' && (
        <VerifyPhoneNumberSuccess
          success={step.success}
          onCompletion={props.onSuccess}
          didVerifyExistingAccount={controlData.isLoggedIn}
        />
      )}
      {step.type === 'timedSuccess' && (
        <TimedSuccess success={step.success} onCompletion={props.onSuccess} />
      )}
    </Flex>
  )
}

export default PhoneNumberVerification
